import React, { useState } from 'react';
import { Table, Tooltip, InputNumber, Card, Icon, Progress } from 'antd';
import { useTranslation, Trans } from 'react-i18next';
import Colors from '../../../../../../themes/Colors';
import NumberFormat from '../../../../../shared/components/NumberFormat';
import {
  calculateValueForPrice,
  formatNumber,
  SeparateNumberFormat
} from '../../../../../../services/utils';
import { getMaxExtractableSpots } from '../../../../../../services/display';
import { checkIsNewDisplayInEdit } from '../../../../../../services/campaignManager';
import NewScreenTag from '../../../../../shared/components/NewScreenTag';
import { impressions, transfer } from '../../../services/investmentCalculations';

const DisplayViewComponent = ({
  contentsDisplaysSelect,
  currency,
  calculateDiffValue,
  calculateImpressionsView,
  getRecordFloorReasons,
  // calculateConsumedImpressions
  editCampaignActive,
  initialBackup,
  hasInitialBackup
}) => {
  const { t } = useTranslation();

  const getMinimumInvestmentInfo = (record) => {
    const display = {
      minimum_programmatic_spots: record.minimum_programmatic_spots,
      minimum_programmatic_period: record.minimum_programmatic_period,
    };
    const minSpots = transfer.minimumInvestmentFloor(display, record.relationShowDays || 0);
    const minInvestment = minSpots * ((record.smart_campaign_cpm || 0) / 1000);
    return {
      minImpressions: SeparateNumberFormat(
        impressions.fromSpots(minSpots, record.multiplier)
      ),
      minInvestment: `$${currency} ${formatNumber(minInvestment)}`,
    };
  };

  // Group customContents by displayId to create display-level aggregations
  const getDisplayData = customContents => {
    const displayMap = new Map();
    customContents.forEach(content => {
      const displayId = content.displayId;
      if (!displayMap.has(displayId)) {
        const reasons = getRecordFloorReasons
          ? getRecordFloorReasons(content)
          : {
              hasDiscount: !!content.appliedDiscount,
              hasMinimum: false,
              appliedDiscount: content.appliedDiscount || null,
            };
        displayMap.set(displayId, {
          display_id: displayId,
          display_name: content.display_name,
          smart_campaign_cpm: content.smart_campaign_cpm,
          // multiplier and CPMi are per display, so the first content carries them
          multiplier: content.multiplier,
          cpmi: content.cpmi,
          contents: [],
          // Get discount info from first content (discount is per display, same for all contents)
          spotsPerDay: content.spotsPerDay || 0,
          appliedDiscount: reasons.appliedDiscount || content.appliedDiscount || null,
          hasDiscount: reasons.hasDiscount,
          hasMinimum: reasons.hasMinimum,
          minimum_programmatic_spots: content.minimum_programmatic_spots || 0,
          hasVolumeDiscounts: !!content.hasVolumeDiscounts,
          minimum_programmatic_period: content.minimum_programmatic_period || 0
        });
      }
      displayMap.get(displayId).contents.push(content);
    });
    // Convert map to array and calculate totals for each display
    const result = Array.from(displayMap.values()).map(display => {
      // Get relationShowDays from first content (same for all contents in display)
      const relationShowDays = (display.contents[0] && display.contents[0].relationShowDays) || 0;
      const totals = display.contents.reduce(
        (acc, content) => {
          const currentImpressions =
            content.promised_shows_update !== undefined
              ? content.promised_shows_update
              : Math.max(0, calculateImpressionsView(content));
          acc.total_pending_spots += currentImpressions;
          acc.total_remaining_spots += calculateDiffValue('subtract', content);
          acc.total_added_spots += calculateDiffValue('add', content);
          acc.total_budget += calculateValueForPrice(
            content.smart_campaign_cpm,
            currentImpressions
          );
          acc.total_spotsPerDay += content.spotsPerDay;
          // acc.total_consumed_spots += calculateConsumedImpressions(content);
          return acc;
        },
        {
          total_pending_spots: 0,
          total_remaining_spots: 0,
          total_added_spots: 0,
          total_budget: 0,
          total_spotsPerDay: 0,
          // total_consumed_spots: 0
        }
      );
      // Calculate max extractable spots for this display
      const maxExtractable = getMaxExtractableSpots(
        totals.total_pending_spots,
        totals.total_spotsPerDay,
        display.appliedDiscount,
        relationShowDays
      );
      return {
        ...display,
        ...totals,
        maxExtractable,
        relationShowDays
      };
    });
    return result;
  };

  const columns = [
    {
      title: t('Screen'),
      dataIndex: 'display_name',
      key: 'display_name',
      render: (text, record) => {
        const isNewDisplay =
          checkIsNewDisplayInEdit(record.display_id, initialBackup, hasInitialBackup);
        return (
          <div>
            <span>{text}</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: 5 }}>
              {isNewDisplay && <NewScreenTag style={{ marginRight: 0 }} />}
              {isNewDisplay && record.hasVolumeDiscounts && (
                <Tooltip
                  placement="top"
                  title={t(
                    'This screen has volume discounts configured, impressions will probably be added to it.'
                  )}
                >
                  <Icon
                    type="tags"
                    style={{
                      fontSize: '18px',
                      verticalAlign: 'middle',
                      color: '#52C41A'
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        );
      }
    },
    // {
    //   title: t('Consumed spots'),
    //   key: 'total_consumed_spots',
    //   render: (text, record) => {
    //     const consumed = record.total_consumed_spots;
    //     const total = consumed + record.total_pending_spots;
    //     const percent = total > 0 ? Math.round((consumed / total) * 100) : 0;
    //     return (
    //       <Tooltip title={`${SeparateNumberFormat(consumed)} / ${SeparateNumberFormat(total)}`}>
    //         <Progress
    //           type="circle"
    //           width={40}
    //           percent={percent}
    //           strokeColor={consumed === 0 ? '#bfbfbf' : Colors.primary}
    //         />
    //       </Tooltip>
    //     );
    //   }
    // },
    {
      title: t('Pending impressions'),
      key: 'total_pending_impressions',
      render: (text, record) => (
        <Tooltip title={t('Shows the remaining promised impressions')}>
          <InputNumber
            disabled
            type="text"
            formatter={SeparateNumberFormat}
            parser={value => Number(value.replace(/\./g, ''))}
            value={impressions.fromSpots(record.total_pending_spots, record.multiplier)}
            style={{ color: Colors.black }}
            className="text-impression-disabled-important"
          />
        </Tooltip>
      )
    },
    {
      title: t('Subtracted impressions'),
      key: 'total_remaining_impressions',
      render: (text, record) => (
        <InputNumber
          disabled
          type="text"
          formatter={SeparateNumberFormat}
          parser={value => Number(value.replace(/\./g, ''))}
          value={impressions.fromSpots(record.total_remaining_spots, record.multiplier)}
          style={{ color: Colors.black }}
          className="text-impression-disabled-important"
        />
      )
    },
    {
      title: t('Added impressions'),
      key: 'total_added_impressions',
      render: (text, record) => (
        <InputNumber
          disabled
          type="text"
          formatter={SeparateNumberFormat}
          parser={value => Number(value.replace(/\./g, ''))}
          value={impressions.fromSpots(record.total_added_spots, record.multiplier)}
          style={{ color: Colors.black }}
          className="text-impression-disabled-important"
        />
      )
    },
    {
      title: t('Budget'),
      key: 'total_budget',
      render: (text, record) => (
        <InputNumber
          disabled
          value={record.total_budget}
          formatter={value => `$${currency} ${formatNumber(value)}`}
          style={{ color: Colors.black }}
          className="text-impression-disabled-important"
        />
      )
    },
    {
      title: t('CPM/impression'),
      dataIndex: 'cpmi',
      key: 'cpmi',
      render: cpmi => <NumberFormat value={cpmi} currency={currency} />
    },
    {
      title: t('Conditions'),
      key: 'restrictions',
      width: 180,
      render: (text, record) => {
        if (!record.hasDiscount && !record.hasMinimum) {
          return <span style={{ color: Colors.gray }}>—</span>;
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {record.hasDiscount && (
              <Tooltip
                title={
                  <div>
                    <Trans
                      i18nKey="volume discount extraction limit impressions"
                      values={{
                        limit: SeparateNumberFormat(
                          impressions.fromSpots(record.maxExtractable, record.multiplier)
                        )
                      }}
                      components={{ strong: <strong /> }}
                    />
                  </div>
                }
              >
                <span
                  className="ant-tag ant-tag-orange"
                  style={{ fontSize: '11px', margin: 0, cursor: 'help' }}
                >
                  <Icon type="exclamation-circle" style={{ marginRight: '4px' }} />
                  {t('Volume discount')}
                </span>
              </Tooltip>
            )}
            {record.hasMinimum && (
              <Tooltip
                title={
                  <div>
                    <Trans
                      i18nKey="minimum investment extraction message impressions"
                      values={getMinimumInvestmentInfo(record)}
                      components={{ strong: <strong /> }}
                    />
                  </div>
                }
              >
                <span
                  className="ant-tag ant-tag-orange"
                  style={{ fontSize: '11px', margin: 0, cursor: 'help' }}
                >
                  <Icon type="exclamation-circle" style={{ marginRight: '4px' }} />
                  {t('Minimum investment')}
                </span>
              </Tooltip>
            )}
          </div>
        );
      }
    }
  ];
  return (
    <div>
      {contentsDisplaysSelect.map(collection => {
        const displayData = getDisplayData(collection.customContents);
        return (
          <Card key={collection.key} title={collection.key} headStyle={{ fontWeight: 'bolder' }}>
            <Table
              columns={columns}
              dataSource={displayData}
              rowKey="display_id"
              pagination={false}
            />
          </Card>
        );
      })}
    </div>
  );
};

export default DisplayViewComponent;
